import { useState, FormEvent, useEffect } from 'react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useAuth } from '../../context/AuthContext';
import * as authApi from '../../api/auth';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Field } from '../common/Field';
import { Input } from '../common/Input';
import { PasswordStrengthMeter } from '../common/PasswordStrengthMeter';
import { apiError } from '../../utils/apiError';
import { describeUserAgent } from '../../utils/userAgent';
import { formatDateTime } from '../../utils/datetime';
import { Session } from '../../types';
import { ApiKeysSection } from './ApiKeysSection';
import { BrowserPushSection } from './BrowserPushSection';

export function ProfilePage() {
  useDocumentTitle('Profile');
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [revoking, setRevoking] = useState<Session | null>(null);

  const mismatch = Boolean(confirmPassword) && newPassword !== confirmPassword;
  // A write key can do whatever its owner can, so only roles that can change
  // something may hold one.
  const canWrite = Boolean(user?.permissions?.some(p => p === '*' || /\.(create|edit|delete)$/.test(p)));

  useEffect(() => {
    if (user) {
      authApi.getUserSessions(user.id)
        .then(setSessions)
        .catch(() => {})
        .finally(() => setSessionsLoading(false));
    }
  }, [user]);

  async function handleProfileSubmit(e: FormEvent) {
    e.preventDefault();
    setProfileMsg('');
    setProfileErr('');
    setProfileLoading(true);
    try {
      await authApi.updateUser(user!.id, { name, email });
      await refreshUser();
      setProfileMsg('Profile updated');
    } catch (err) {
      setProfileErr(apiError(err, 'Failed to update profile'));
    } finally {
      setProfileLoading(false);
    }
  }

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setPwMsg('');
    setPwErr('');

    if (newPassword !== confirmPassword) {
      setPwErr('New passwords do not match');
      return;
    }

    setPwLoading(true);
    try {
      await authApi.changePassword(user!.id, { currentPassword, newPassword });
      setPwMsg('Password changed');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwErr(apiError(err, 'Failed to change password'));
    } finally {
      setPwLoading(false);
    }
  }

  async function handleRevokeSession() {
    if (!revoking) return;
    const id = revoking.id;
    try {
      await authApi.revokeSession(user!.id, id);
      setSessions(prev => prev.filter(s => s.id !== id));
    } catch {
      // The list refreshes on next visit; nothing actionable to show here.
    } finally {
      setRevoking(null);
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <h1 className="text-2xl font-bold tracking-tight">Profile</h1>

      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Account information</h2>
        <form onSubmit={handleProfileSubmit} className="space-y-4">
          {profileMsg && <Alert tone="success">{profileMsg}</Alert>}
          {profileErr && <Alert tone="error">{profileErr}</Alert>}

          <Field label="Name" required>
            {p => <Input {...p} type="text" autoComplete="name" required value={name} onChange={e => setName(e.target.value)} />}
          </Field>
          <Field label="Email" required>
            {p => <Input {...p} type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} />}
          </Field>
          <Field label="Role" hint="Roles are changed by an administrator.">
            {p => <Input {...p} type="text" disabled value={user?.role || ''} />}
          </Field>

          <Button type="submit" loading={profileLoading}>
            {profileLoading ? 'Saving…' : 'Save changes'}
          </Button>
        </form>
      </Card>

      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Change password</h2>
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          {pwMsg && <Alert tone="success">{pwMsg}</Alert>}
          {pwErr && <Alert tone="error">{pwErr}</Alert>}

          <Field label="Current password" required>
            {p => (
              <Input
                {...p}
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
              />
            )}
          </Field>

          <div>
            <Field label="New password" required>
              {p => (
                <Input
                  {...p}
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                />
              )}
            </Field>
            <PasswordStrengthMeter password={newPassword} />
          </div>

          <Field
            label="Confirm new password"
            required
            error={mismatch ? 'Passwords do not match' : undefined}
          >
            {p => (
              <Input
                {...p}
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
              />
            )}
          </Field>

          <Button type="submit" loading={pwLoading}>
            {pwLoading ? 'Changing…' : 'Change password'}
          </Button>
        </form>
      </Card>

      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-1">Active sessions</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Every device currently signed in to your account.
        </p>

        {sessionsLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading sessions…</p>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No active sessions.</p>
        ) : (
          <ul className="space-y-3">
            {sessions.map(session => (
              <li
                key={session.id}
                className="flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-md"
              >
                <div className="text-sm min-w-0">
                  <p className="font-medium truncate">{describeUserAgent(session.user_agent)}</p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs font-mono tabular-nums truncate">
                    {session.ip_address || 'Unknown IP'} · active {formatDateTime(session.last_active_at)}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setRevoking(session)}>
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <ApiKeysSection canWrite={canWrite} />

      <BrowserPushSection />

      <ConfirmDialog
        open={!!revoking}
        title="Revoke session"
        message={
          `${describeUserAgent(revoking?.user_agent ?? null)} will be signed out immediately. ` +
          `If this is the device you are using now, you will be signed out too.`
        }
        confirmLabel="Revoke session"
        onConfirm={handleRevokeSession}
        onClose={() => setRevoking(null)}
      />
    </div>
  );
}
